import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Tag, Button, Spin, Tabs, Switch, message, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useHistory } from 'react-router-dom';
import { ArrowLeftOutlined, EyeOutlined } from '@ant-design/icons';
import Api from '../../Network';
import NameByLang from '../../Component/nameByLang';
import { usePermissions } from '../../contexts/PermissionContext';
import MobileTable from '../../Component/MobileTable';
import SalesAnalyticsTab from './SalesAnalyticsTab';
import RelatedCustomersTab from './RelatedCustomersTab';
import './DriverDetail.scss';
import InvoiceLineItemsAnalyticsTab from './InvoiceLineItemsAnalyticsTab';

const { TabPane } = Tabs;

const DriverDetail = () => {
  const { t } = useTranslation();
  const { hasPermission, hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const queryClient = useQueryClient();

  // Check if user has permission to view orders list (not order details)
  // view_orders = list of orders, view_order = order details
  const canViewOrders =
    !permissionsLoading &&
    (hasPermission('view_orders') || hasPermission('orders.read') || hasPermission('orders.view'));

  // Same rules as sidebar /reports/sales-analytics — not implied by view_orders
  const canViewSalesAnalytics =
    !permissionsLoading && hasAnyPermission(['view_sales_analytics', 'download_sales_analytics']);

  // Check if user has permission to update sales representative
  const canUpdateSalesRepresentative =
    !permissionsLoading &&
    (hasPermission('update_sales_representative') ||
      hasPermission('sales_representatives.update') ||
      hasPermission('sales_representatives.edit') ||
      hasPermission('drivers.update') ||
      hasPermission('drivers.edit'));

  // Set default active tab - always default to 'info'
  const [activeTab, setActiveTab] = useState('info');

  // Reset active tab if user doesn't have permission and is on orders tab
  useEffect(() => {
    if (!canViewOrders && activeTab === 'orders') {
      setActiveTab('info');
    }
  }, [canViewOrders, activeTab]);

  useEffect(() => {
    const analyticsKeys = ['salesAnalytics', 'relatedCustomers', 'invoiceLineItemsAnalytics'];
    if (!canViewSalesAnalytics && analyticsKeys.includes(activeTab)) {
      setActiveTab('info');
    }
  }, [canViewSalesAnalytics, activeTab]);

  // Fetch driver details (optimized - no orders)
  const {
    data: driverData,
    isLoading: driverLoading,
    error: driverError,
  } = useQuery({
    queryKey: ['driver-optimized', id],
    queryFn: async () => {
      const response = await Api.get(`api/admin/drivers/${id}/optimized`);
      return response.data;
    },
    enabled: !!id,
  });

  // Update driver status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: 'Active' | 'Inactive') => {
      const response = await Api.put(`api/admin/drivers/${id}/status`, {
        status: newStatus,
      });
      return response.data;
    },
    onSuccess: () => {
      message.success(
        t('salesRepresentatives.statusUpdated', 'Driver status updated successfully')
      );
      // Invalidate and refetch driver data
      queryClient.invalidateQueries({ queryKey: ['driver-optimized', id] });
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        t('salesRepresentatives.updateError', 'Failed to update driver status');
      message.error(errorMessage);
    },
  });

  const handleStatusChange = (checked: boolean) => {
    if (!canUpdateSalesRepresentative) {
      message.warning(
        t('permissions.accessDenied') ||
          'You do not have permission to update sales representative status'
      );
      return;
    }
    const newStatus = checked ? 'Active' : 'Inactive';
    updateStatusMutation.mutate(newStatus);
  };

  // Fetch driver orders only when orders tab is active and user has permission
  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['driver-orders', id],
    queryFn: async () => {
      const response = await Api.get(`api/admin/drivers/${id}/orders`);
      return response.data;
    },
    enabled: !!id && activeTab === 'orders' && canViewOrders,
  });

  // Get orders from separate API call
  const driverOrders = ordersData || [];

  const handleBack = () => {
    history.push('/sales-representatives');
  };

  if (driverLoading) {
    return (
      <div className="driver-detail-page">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>{t('common.loading', 'Loading...')}</div>
        </div>
      </div>
    );
  }

  if (driverError || !driverData) {
    return (
      <div className="driver-detail-page">
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <div style={{ color: '#ff4d4f', marginBottom: '16px' }}>
              {t('common.error', 'Error loading driver details')}
            </div>
            <Button onClick={handleBack}>{t('common.back', 'Back')}</Button>
          </div>
        </Card>
      </div>
    );
  }

  const driver = driverData;

  // Orders table columns
  const orderColumns = [
    {
      title: t('orders.columns.id', 'Order ID'),
      dataIndex: 'id',
      key: 'id',
      render: (id: number) => `#${id}`,
    },
    {
      title: t('orders.columns.customer', 'Customer'),
      dataIndex: 'customer',
      key: 'customer',
      render: (customer: any) => <NameByLang name={customer?.name} className="bold" />,
    },
    {
      title: t('orders.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'default';
        switch (status) {
          case 'Approved':
            color = 'green';
            break;
          case 'Draft':
            color = 'blue';
            break;
          case 'Declined':
            color = 'red';
            break;
          case 'Canceled':
            color = 'orange';
            break;
          default:
            color = 'default';
        }

        return <Tag color={color}>{t(`orders.status.${status}`, status)}</Tag>;
      },
    },
    {
      title: t('orders.columns.total', 'Total'),
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => `${total} ${t('common.SAR', 'SAR')}`,
    },
    {
      title: t('orders.columns.createdAt', 'Created At'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      render: (record: any) => {
        // Check permission to view order details (not order list)
        // view_order = order details, view_orders = order list
        const canViewOrderDetails =
          hasPermission('view_order') ||
          hasPermission('orders.read') ||
          hasPermission('orders.view');

        if (!canViewOrderDetails) {
          return null;
        }

        return (
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => history.push(`/orders/${record.id}`)}
          >
            {t('common.view', 'View')}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="driver-detail-page">
      <Card>
        <div className="driver-header">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ marginBottom: '16px' }}
          >
            {t('common.back', 'Back')}
          </Button>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={t('driver.info', 'Driver Information')} key="info">
            <Descriptions bordered column={2}>
              <Descriptions.Item label={t('salesRepresentatives.form.name', 'Name')}>
                <NameByLang name={driver.name} className="bold" />
              </Descriptions.Item>

              <Descriptions.Item label={t('salesRepresentatives.form.phoneNumber', 'Phone Number')}>
                {driver.phone_number}
              </Descriptions.Item>

              <Descriptions.Item label={t('salesRepresentatives.form.inventory', 'Inventory')}>
                {Array.isArray(driver.inventories) && driver.inventories.length > 0 ? (
                  <Tag color="blue">
                    <NameByLang
                      name_en={driver.inventories[0]?.name}
                      name_ar={driver.inventories[0]?.ar_name}
                      fallback={t('common.noData', 'No Data')}
                    />
                  </Tag>
                ) : (
                  <Tag color="default">{t('common.noData', 'No Data')}</Tag>
                )}
              </Descriptions.Item>

              <Descriptions.Item label={t('common.status', 'Status')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tag color={driver.status === 'Active' ? 'green' : 'red'}>{driver.status}</Tag>
                  {canUpdateSalesRepresentative && (
                    <Popconfirm
                      title={
                        driver.status === 'Active'
                          ? t(
                              'salesRepresentatives.deactivateConfirm',
                              'Are you sure you want to deactivate this driver?'
                            )
                          : t(
                              'salesRepresentatives.activateConfirm',
                              'Are you sure you want to activate this driver?'
                            )
                      }
                      onConfirm={() => handleStatusChange(driver.status !== 'Active')}
                      okText={t('common.yes', 'Yes')}
                      cancelText={t('common.no', 'No')}
                      disabled={updateStatusMutation.isPending}
                    >
                      <Switch
                        checked={driver.status === 'Active'}
                        checkedChildren={t('common.active', 'Active')}
                        unCheckedChildren={t('common.inactive', 'Inactive')}
                        loading={updateStatusMutation.isPending}
                        style={{ marginLeft: '8px' }}
                      />
                    </Popconfirm>
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label={t('common.createdAt', 'Created At')}>
                {new Date(driver.created_at).toLocaleDateString()}
              </Descriptions.Item>

              <Descriptions.Item label={t('common.updatedAt', 'Updated At')}>
                {new Date(driver.updated_at).toLocaleDateString()}
              </Descriptions.Item>
            </Descriptions>
          </TabPane>

          {canViewOrders && (
            <TabPane
              className="driver-detail-tabpane-extra"
              tab={t('driver.orders', 'Orders')}
              key="orders"
            >
              <div className="orders-section">
                <div className="orders-header">
                  <h3>{t('driver.orders', 'Driver Orders')}</h3>
                  <div className="orders-stats">
                    <Tag color="blue">
                      {t('orders.total', 'Total')}: {driverOrders.length}
                    </Tag>
                  </div>
                </div>

                <MobileTable
                  columns={orderColumns}
                  dataSource={driverOrders}
                  loading={ordersLoading}
                  rowKey="id"
                  onRowClick={(record: any) => history.push(`/orders/${record.id}`)}
                  pagination={{
                    total: driverOrders.length,
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total: number, range: [number, number]) =>
                      `${t('table.showing', 'Showing')} ${range[0]}-${range[1]} ${t('table.of', 'of')} ${total} ${t('table.entries', 'entries')}`,
                  }}
                  locale={{
                    emptyText: t('orders.noOrders', 'No orders found for this driver'),
                  }}
                />
              </div>
            </TabPane>
          )}

          {canViewSalesAnalytics && (
            <TabPane
              className="driver-detail-tabpane-extra"
              tab={t('salesRepresentatives.tabs.salesAnalytics', 'Sales analytics')}
              key="salesAnalytics"
            >
              <SalesAnalyticsTab
                enabled={activeTab === 'salesAnalytics'}
                driverId={id}
                driverName={driver.name}
              />
            </TabPane>
          )}

          {canViewSalesAnalytics && (
            <TabPane
              className="driver-detail-tabpane-extra"
              tab={t('salesRepresentatives.tabs.relatedCustomers', 'Related customers')}
              key="relatedCustomers"
            >
              <RelatedCustomersTab enabled={activeTab === 'relatedCustomers'} driverId={id} />
            </TabPane>
          )}

          {canViewSalesAnalytics && (
            <TabPane
              className="driver-detail-tabpane-extra"
              tab={t(
                'salesRepresentatives.tabs.invoiceLineItemsAnalytics',
                'Invoice item analytics'
              )}
              key="invoiceLineItemsAnalytics"
            >
              <InvoiceLineItemsAnalyticsTab
                enabled={activeTab === 'invoiceLineItemsAnalytics'}
                driverId={id}
              />
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};

export default DriverDetail;
